import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import styled, { css } from "styled-components";
import moment from "moment-timezone";
import { useMappedState } from "redux-react-hook";
import { toast } from "react-toastify";
import _ from "lodash";

import DropSwitcher, { DropSwitcherTheme } from "src/core/drops/components/DropSwitcherButton";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { breakpoint } from "src/library/utils/styled-media-queries/index";
import { PageProps } from "src/pages/Router/routes";
import { useDrop } from "src/core/drops/hooks";
import {
  ListOrdersByDropParams,
  ordersApi,
  OrdersResponse
} from "src/core/orders/api/orders.api";
import { IStore } from "src/core/shared/store";
import { getOrderStatusIdFromCode } from "src/core/orders/utils/orderStatus";
import OrderDayPreviewCard, { OrderDayPreviewCardProps } from "src/core/orders/components/OrderDayPreviewCard";
import { getDeliveryRoute } from "src/core/drops/utils/inherited-settings";
import { DaysOfWeek } from "src/utils/constants";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import useMinimumLeadTime from "src/core/shared/hooks/useMinimumLeadTime";
import {
  useCutoffTimeForWeekDay,
  useDaysToCutoff
} from "src/core/orders/hooks/validation";
import { getWeekDayFromDate } from "src/core/orders/utils/order-deliveries";
import { hourMinuteTimeSplit } from "src/library/utils/time-helpers/time-helpers";
import useTimezone from "src/core/shared/hooks/time-helpers/useTimeZone";
import { Text } from "src/library/components/Text/Text";
import { useDropShutdowns } from "src/modules/shutdowns/hooks/useDropShutdowns";

const VanSalesOrdersPage: React.ReactFCWithChildren<PageProps<unknown>> = ({ title, ...props }) => {
  const [ { drop } ] = useDrop();
  const [ ordersResponse, getOrders ] = ordersApi<OrdersResponse>("LIST_DROP_ORDERS");
  const { minimumLeadTime } = useMinimumLeadTime();
  const daysToCutoff = useDaysToCutoff();
  const getCuttOffTimeForWeekDay = useCutoffTimeForWeekDay();
  const timezone = useTimezone();

  const disabledDaysOfWeek = useMemo(() => {
    const deliveryRoute = drop && getDeliveryRoute(drop.groupedSettings);

    return deliveryRoute && _.difference(DaysOfWeek, _.values(deliveryRoute.deliveryDays));
  }, [ drop ]);

  const { checkShutdownDate } = useDropShutdowns();
  const { orderStatuses } = useMappedState((store: IStore) => ({ orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [] }));

  // instantiate next 7 days of orders list with base card props
  const [ nextSevenDaysOrders, setNextSevenDaysOrders ] = useState<OrderDayPreviewCardProps[]>(() => {
    const today = moment.utc().toDate();
    const nextSevenDays: OrderDayPreviewCardProps[] = [];

    for (let i = 0; i < 7; ++i) {
      nextSevenDays.push({
        order: null,
        date: moment.utc(today).add(i, "days").toDate()
      });
    }

    return nextSevenDays;
  });

  // evaluates and unorderable blocks dates
  const disabled = useCallback((date: Date) => {
    if (!minimumLeadTime) {
      return true;
    }
    const hasShutdown = checkShutdownDate(date, "adhoc");

    // if the date matches a shutdown dont allow creation
    if (hasShutdown) {
      return true;
    }
    const leadTime = minimumLeadTime;
    // get the days till this date cuts off
    const daysToCut = daysToCutoff(date, leadTime);

    // return true if the delivery date does not have enough advance notice.
    if (typeof daysToCut !== "number" || daysToCut < 0) {
      return true;
    }

    if (daysToCut === 0) {
      const cutoffTime = getCuttOffTimeForWeekDay(leadTime, getWeekDayFromDate(date));

      if (cutoffTime) {
        const { hours, minutes } = hourMinuteTimeSplit(cutoffTime);

        if (moment.utc().isAfter(moment.tz(timezone).hours(hours).minutes(minutes))) {
          return true;
        }
      }
    }

    return false;
  }, [
    checkShutdownDate,
    minimumLeadTime,
    daysToCutoff,
    getCuttOffTimeForWeekDay,
    timezone
  ]);

  // when drop ready, get next 7 days orders for the drop
  useEffect(() => {
    if (drop) {
      const startDate = moment.utc();
      const endDate = moment.utc(startDate).add(8, "days");
      const cancelledOrderStatus = getOrderStatusIdFromCode(orderStatuses, "cancelled");

      getOrders<ListOrdersByDropParams>({
        pagination: {
          page: 0,
          limit: 20,
          offset: 0,
          sort: [ [ "updatedAt", "DESC" ] ]
        },
        filters: {
          orderStatusId: { $not: cancelledOrderStatus },
          orderDeliveryDate: { $between: [ startDate.startOf("day").toDate(), endDate.endOf("day").toDate() ] }
        },
        dropId: drop.id
      });
    }
  // Inifinite loop caused by getOrders. Added to github issue https://github.com/Mezze-io/mezze-ui/issues/111
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ drop, orderStatuses ]);

  useEffect(() => {
    // populate next 7 days orders, also setting unavailable order days
    if (ordersResponse.data?.orders) {
      const orderResOrders = ordersResponse.data.orders.result;

      // loop orders and attach order to relevant day
      setNextSevenDaysOrders(currentNextSevenDaysOrders => _.map(currentNextSevenDaysOrders, nextSevenDaysOrder => {
        // note: this finds first instance meaning it will only return 1 order even if there are multipe orders on the given day
        // todo - add a fallback where if multiple orders on a given date, link them to a pre filtered orders page
        const orderExistsIndex = _.findIndex(orderResOrders, order => moment.utc(order.orderDeliveryDate).isSame(moment.utc(nextSevenDaysOrder.date), "day"));
        const unavailableDay = disabledDaysOfWeek?.includes(moment.utc(nextSevenDaysOrder.date).isoWeekday());

        const updatedNextSevenDaysOrder = {
          ...nextSevenDaysOrder,
          order: null,
          unavailableOrderDay: unavailableDay ? unavailableDay : disabled(nextSevenDaysOrder.date)
        };

        if (orderExistsIndex !== -1) {
          return {
            ...updatedNextSevenDaysOrder,
            order: orderResOrders[ orderExistsIndex ]
          };
        }

        return updatedNextSevenDaysOrder;
      }));
    }

    if (ordersResponse.error) {
      toast.error(ordersResponse.error.message);
    }
  }, [
    disabled,
    disabledDaysOfWeek,
    ordersResponse
  ]);

  return (
    <Layout
      title={title}
      {...props}
    >
      <MainContentWrapper fill>
        <VanSalesOrdersWrapper>
          <Text className="switcher-label">
            Select delivery point
          </Text>

          <DropSwitcher
            dropSwitcherTheme={DropSwitcherTheme.Light}
            optionsFullWidth
            disableGetDropPageChange
          />

          {ordersResponse.pending && (
            <LoadingSpinner
              type="circular"
              position="inline"
            />
          )}

          <div className="day-cards">
            {!ordersResponse.pending && ordersResponse.data && nextSevenDaysOrders.map(nextSevenDaysOrder => (
              <OrderDayPreviewCard
                key={moment.utc(nextSevenDaysOrder.date).format("YYYY-MM-DD")}
                order={nextSevenDaysOrder.order}
                date={nextSevenDaysOrder.date}
                unavailableOrderDay={nextSevenDaysOrder.unavailableOrderDay}
              />
            ))}
          </div>
        </VanSalesOrdersWrapper>
      </MainContentWrapper>
    </Layout>
  );
};

const VanSalesOrdersWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;

    .day-cards {
      padding: ${theme.spacing.padding.sm};
    }
    
    .switcher-label {
      margin-left: ${theme.spacing.padding.sm};
    }

    ${breakpoint("lg", theme)} {
      width: 50%;
    }
  `}
`;

export default VanSalesOrdersPage;
